/**
 * Public Appointment Confirmation Page
 *
 * Allows patients to confirm or cancel appointments via unique link.
 * This is a public page - no authentication required.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { supabase } from '../../services/supabaseService';

// =============================================================================
// TYPES
// =============================================================================

interface AppointmentDetails {
  id: string;
  clientName: string;
  organizationName: string;
  organizationLogo?: string;
  organizationPhone?: string;
  startTime: string;
  endTime: string;
  location?: string;
  status: string;
  confirmationResponse?: string;
  confirmedAt?: string;
}

type ConfirmationState = 'loading' | 'pending' | 'confirmed' | 'cancelled' | 'already_responded' | 'expired' | 'error';

// =============================================================================
// COMPONENT
// =============================================================================

const ConfirmAppointment: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const theme = useTheme();

  const [state, setState] = useState<ConfirmationState>('loading');
  const [appointment, setAppointment] = useState<AppointmentDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if action was passed in URL (for SMS quick confirm)
  const actionFromUrl = searchParams.get('action');

  // Load appointment details
  useEffect(() => {
    const loadAppointment = async () => {
      if (!token) {
        setState('error');
        setErrorMessage('Link de confirmação inválido.');
        return;
      }

      try {
        // Fetch appointment by confirmation token
        const { data: appointmentData, error: appointmentError } = await supabase
          .from('appointments')
          .select(`
            id,
            start_time,
            end_time,
            location,
            status,
            confirmation_response,
            confirmed_at,
            client_id,
            organization_id
          `)
          .eq('confirmation_token', token)
          .single();

        if (appointmentError || !appointmentData) {
          setState('error');
          setErrorMessage('Agendamento não encontrado ou link expirado.');
          return;
        }

        // Check if appointment is in the past
        if (new Date(appointmentData.start_time) < new Date()) {
          setState('expired');
          return;
        }

        // Check if already responded
        if (appointmentData.confirmation_response) {
          setState('already_responded');
          setAppointment({
            id: appointmentData.id,
            clientName: '',
            organizationName: '',
            startTime: appointmentData.start_time,
            endTime: appointmentData.end_time,
            location: appointmentData.location,
            status: appointmentData.status,
            confirmationResponse: appointmentData.confirmation_response,
            confirmedAt: appointmentData.confirmed_at,
          });
          return;
        }

        // Fetch client name
        const { data: clientData } = await supabase
          .from('clients')
          .select('full_name')
          .eq('id', appointmentData.client_id)
          .single();

        // Fetch organization details
        const { data: orgData } = await supabase
          .from('organizations')
          .select('name, logo_url, settings')
          .eq('id', appointmentData.organization_id)
          .single();

        setAppointment({
          id: appointmentData.id,
          clientName: clientData?.full_name || 'Paciente',
          organizationName: orgData?.name || 'Clínica',
          organizationLogo: orgData?.logo_url,
          organizationPhone: orgData?.settings?.contactPhone,
          startTime: appointmentData.start_time,
          endTime: appointmentData.end_time,
          location: appointmentData.location,
          status: appointmentData.status,
          confirmationResponse: appointmentData.confirmation_response,
          confirmedAt: appointmentData.confirmed_at,
        });

        setState('pending');

        // Auto-confirm if action=confirm in URL (for SMS quick confirm)
        if (actionFromUrl === 'confirm') {
          handleConfirm();
        } else if (actionFromUrl === 'cancel') {
          handleCancel();
        }
      } catch (error) {
        console.error('Error loading appointment:', error);
        setState('error');
        setErrorMessage('Erro ao carregar detalhes do agendamento.');
      }
    };

    loadAppointment();
  }, [token, actionFromUrl]);

  const handleConfirm = async () => {
    if (!appointment || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          status: 'confirmed',
          confirmation_response: 'confirmed',
          confirmed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', appointment.id);

      if (error) throw error;

      setState('confirmed');
    } catch (error) {
      console.error('Error confirming appointment:', error);
      setErrorMessage('Erro ao confirmar agendamento. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!appointment || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          status: 'canceled',
          confirmation_response: 'cancelled',
          confirmed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', appointment.id);

      if (error) throw error;

      setState('cancelled');
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      setErrorMessage('Erro ao cancelar agendamento. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getLocationLabel = (location?: string) => {
    switch (location) {
      case 'video_call':
        return 'Videochamada';
      case 'home_visit':
        return 'Visita Domiciliar';
      case 'office':
      default:
        return 'Presencial no Consultório';
    }
  };

  // =============================================================================
  // RENDER STATES
  // =============================================================================

  // Loading state
  if (state === 'loading') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Error state
  if (state === 'error') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
          p: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <CancelIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Ops! Algo deu errado
          </Typography>
          <Typography color="text.secondary">
            {errorMessage}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Expired state
  if (state === 'expired') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
          p: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <TimeIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Consulta já realizada
          </Typography>
          <Typography color="text.secondary">
            Esta consulta já passou. Para agendar uma nova consulta,
            entre em contato com a clínica.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Already responded state
  if (state === 'already_responded') {
    const wasConfirmed = appointment?.confirmationResponse === 'confirmed';
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
          p: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          {wasConfirmed ? (
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          ) : (
            <CancelIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          )}
          <Typography variant="h5" gutterBottom>
            {wasConfirmed ? 'Presença já confirmada' : 'Consulta já cancelada'}
          </Typography>
          <Typography color="text.secondary">
            Você já respondeu a esta solicitação de confirmação
            {appointment?.confirmedAt && ` em ${formatDate(appointment.confirmedAt)}`}.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Confirmed state
  if (state === 'confirmed') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
          p: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.success.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
          </Box>
          <Typography variant="h4" gutterBottom fontWeight="bold" color="success.main">
            Presença Confirmada!
          </Typography>
          <Typography color="text.secondary" paragraph>
            Obrigado por confirmar sua consulta.
          </Typography>
          {appointment && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(appointment.startTime)}
              </Typography>
              <Typography variant="h5" color="primary.main" fontWeight="bold">
                {formatTime(appointment.startTime)}
              </Typography>
            </Box>
          )}
          {appointment?.organizationPhone && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Dúvidas? Entre em contato:
              </Typography>
              <Button
                startIcon={<PhoneIcon />}
                href={`tel:${appointment.organizationPhone}`}
                sx={{ mt: 1 }}
              >
                {appointment.organizationPhone}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  // Cancelled state
  if (state === 'cancelled') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
          p: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.error.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <CancelIcon sx={{ fontSize: 48, color: 'error.main' }} />
          </Box>
          <Typography variant="h4" gutterBottom fontWeight="bold" color="error.main">
            Consulta Cancelada
          </Typography>
          <Typography color="text.secondary" paragraph>
            Sua consulta foi cancelada com sucesso.
          </Typography>
          <Typography color="text.secondary">
            Para reagendar, entre em contato com a clínica.
          </Typography>
          {appointment?.organizationPhone && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<PhoneIcon />}
                href={`tel:${appointment.organizationPhone}`}
              >
                {appointment.organizationPhone}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  // =============================================================================
  // PENDING STATE (Main confirmation view)
  // =============================================================================

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
          {/* Header with Organization */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {appointment?.organizationLogo && (
              <Box
                component="img"
                src={appointment.organizationLogo}
                alt={appointment.organizationName}
                sx={{
                  width: 80,
                  height: 80,
                  objectFit: 'contain',
                  borderRadius: 2,
                  mb: 2,
                }}
              />
            )}
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {appointment?.organizationName}
            </Typography>
            <Typography color="text.secondary">
              Confirmação de Consulta
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Appointment Details */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" gutterBottom>
              Olá <strong>{appointment?.clientName}</strong>,
            </Typography>
            <Typography color="text.secondary" paragraph>
              Por favor, confirme sua presença na consulta agendada:
            </Typography>

            <Box
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 2,
                p: 3,
                mt: 3,
              }}
            >
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <EventIcon color="primary" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Data
                    </Typography>
                    <Typography fontWeight="medium">
                      {appointment && formatDate(appointment.startTime)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TimeIcon color="primary" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Horário
                    </Typography>
                    <Typography fontWeight="medium">
                      {appointment && `${formatTime(appointment.startTime)} - ${formatTime(appointment.endTime)}`}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LocationIcon color="primary" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Local
                    </Typography>
                    <Typography fontWeight="medium">
                      {getLocationLabel(appointment?.location)}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Box>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
          )}

          {/* Action Buttons */}
          <Stack spacing={2}>
            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              startIcon={<CheckCircleIcon />}
              onClick={handleConfirm}
              disabled={isSubmitting}
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
              }}
            >
              {isSubmitting ? 'Confirmando...' : 'Confirmar Presença'}
            </Button>

            <Button
              variant="outlined"
              color="error"
              size="large"
              fullWidth
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={isSubmitting}
              sx={{
                py: 1.5,
              }}
            >
              {isSubmitting ? 'Cancelando...' : 'Cancelar Consulta'}
            </Button>
          </Stack>

          {/* Contact Info */}
          {appointment?.organizationPhone && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Precisa reagendar? Entre em contato:
              </Typography>
              <Button
                startIcon={<PhoneIcon />}
                href={`tel:${appointment.organizationPhone}`}
                sx={{ mt: 1 }}
              >
                {appointment.organizationPhone}
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ConfirmAppointment;
